import { useEffect, useId, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Copy, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { Botao, Selo } from '../../componentes/base';
import { Estado } from '../../componentes/estados';
import { Alternador, EntradaSensivel, Rotulo } from '../../componentes/formulario';
import { Confirmar } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { api, del, get, mensagemDeErro, patch, post } from '../../nucleo/api';
import { dataHora, textoLivre } from '../../nucleo/formato';
import { modulosWeb, type ModuloWeb } from '../../nucleo/modulos-web';
import { useSessao } from '../../nucleo/sessao';
import { EditorRico, htmlDoCampo } from '../../componentes/editor-rico';
import { CabecalhoConfig, CaixaConfig, CLASSE_TABELA_CONFIG, CLASSE_TD, CLASSE_TH, LinhaAlternador } from './config-crm-base';
import { CAMINHO_CONFIGURACOES } from './crm-caminhos';
import { useListas } from './crm-listas';
import { TAGS_EMAIL } from './email-tags';
import { abasPreferencias } from './CentralArquivos';

/**
 * Ajustes do CRM (27/10, K21 e F4, trilha "ficha"): o que as Configurações do CRM ganharam para ficar como as do RD. A página
 * inicial (sugestões, busca "Digite para buscar" e a grade de grupos), os Modelos de e-mail (usados no "Criar e-mail" da
 * negociação) e as Preferências (o parabéns depois de marcar a venda). As integrações e vitrines do RD (importar, marketing,
 * CRM2CRM, telefone, enriquecimento) ficam de fora (decisão 18).
 */

/**
 * Grupos da página inicial, na ordem e nas colunas do RD (`SETTINGS` do clone); cada item é uma seção desta página, outra tela
 * do sistema (`para` + `permissao`) ou, quando a funcionalidade ainda não existe, um item desativado com o selo "Em breve"
 * (N2, 28/10: nada da origem fica escondido).
 */
/** `permissao`: uma ou, em lista, todas (rodada rlote-6: o Importar dados do CRM pede `cadastros.importar` e `pipeline.criar`). */
type ItemIndice = { chave: string; rotulo: string; para?: string; permissao?: string | readonly string[]; emBreve?: boolean; novo?: boolean; /** O módulo que precisa estar instalado (sem ele o atalho some). */ modulo?: string };

/**
 * Review rval1-1 (SPEC-B2): a página (`<slug>.<aba>`) que um atalho do índice abre fora destas Configurações, ou null.
 * `/<menu>/<slug>/<aba>` é a aba do módulo; `/crm/<pagina>` é a página da casca nova com a `pagina` que ela declara. As
 * seções desta página (`/crm/configuracoes/...`) não são página de módulo.
 */
export function paginaDoAtalho(para: string, lista: ModuloWeb[] = modulosWeb): string | null {
  const caminho = para.split(/[?#]/)[0]!;
  if (caminho === CAMINHO_CONFIGURACOES || caminho.startsWith(`${CAMINHO_CONFIGURACOES}/`)) return null;
  const partes = caminho.split('/').filter(Boolean);
  const casca = lista.flatMap((m) => m.paginasCasca ?? []).find((p) => p.caminho === partes.slice(0, 2).join('/'));
  if (casca) return casca.pagina ?? null;
  return partes.length >= 3 ? `${partes[1]}.${partes[2]}` : null;
}

/** Lote crm7: os itens que a origem marca com o selo NOVO (`SETTINGS` do clone), pela chave do índice daqui. */
export const ITENS_NOVOS = ['etapasLead', 'qualificacao', 'questionarios', 'workflows', 'modelos', 'enriquecimento', 'assinatura', 'ia', 'logsErros'];
type GrupoIndice = { rotulo: string; coluna: 0 | 1 | 2; itens: ItemIndice[] };

export function useGruposIndice(): GrupoIndice[] {
  const { t } = useTranslation('pipeline');
  const s = useSessao();
  const secao = (chave: string, rotulo = t(`crm.configuracoes.secoes.${chave}`)): ItemIndice => ({ chave, rotulo, para: `${CAMINHO_CONFIGURACOES}/${chave}` });
  const fora = (chave: string, para: string, permissao?: string | readonly string[]): ItemIndice => ({ chave, rotulo: t(`crm.ficha2.config.itens.${chave}`), para, permissao });
  const breve = (chave: string): ItemIndice => ({ chave, rotulo: t(`crm.ficha2.config.itens.${chave}`), emBreve: true });
  const g = (chave: string) => t(`crm.ficha2.config.grupos.${chave}`);
  const grupos: GrupoIndice[] = [
    { rotulo: g('time'), coluna: 0, itens: [{ chave: 'usuarios', rotulo: t('crm.ficha2.config.usuarios'), para: '/pessoas/admin/usuarios', permissao: 'admin.usuarios' }, fora('equipes', '/comercial/pipeline/equipes', 'pipeline.ver')] },
    { rotulo: g('processo'), coluna: 0, itens: [secao('funis'), breve('etapasLead'), secao('campos'), secao('qualificacao'), secao('questionarios')] },
    { rotulo: g('automatize'), coluna: 0, itens: [fora('automacao', '/automacoes/automacoes/fluxos', 'automacoes.ver'), secao('workflows')] },
    { rotulo: g('produtosMarketing'), coluna: 0, itens: [breve('marketing')] },
    {
      rotulo: g('avancado'),
      coluna: 1,
      itens: [
        // rcrm4 SPEC-M1: a importação de contatos e empresas (CSV) que já existe na página Contatos.
        fora('importar', '/crm/contatos?importar=1', ['cadastros.importar', 'pipeline.criar']),
        breve('multivendas'),
        fora('metas', '/comercial/pipeline/metas', 'pipeline.ver'),
        secao('modelos'),
        secao('status-proposta'),
        secao('preferencias', t('crm.ficha2.config.secoes.preferencias')),
        breve('enriquecimento'),
        fora('integracoes', '/integracoes/integracoes/conectores', 'integracoes.ver'),
        // rcrm4 SPEC-M1: a base legal (consentimento) é cuidada em Contatos (cartão de privacidade e ação em massa); o idioma
        // padrão do workspace fica na Aparência (fuso e moeda não têm tela no workspace).
        fora('privacidade', '/crm/contatos', 'cadastros.ver'),
        fora('regionais', '/admin/configuracoes/aparencia', 'configuracoes.ver'),
        fora('whatsapp', '/comercial/conversas/whatsapp', 'conversas.ver'),
        breve('reWhatsapp'),
      ],
    },
    { rotulo: g('potencialize'), coluna: 1, itens: [fora('assinatura', '/cadastros/documentos/assinaturas', 'documentos.ver'), breve('ia')] },
    {
      rotulo: g('conta'),
      coluna: 2,
      itens: [
        secao('fontes'),
        // Lista de 03/11, item 9: seção própria (o catálogo de venda do Cadastros), no lugar do atalho para o Estoque.
        { ...secao('produtos', t('crm.ficha2.config.itens.produtos')), permissao: 'cadastros.ver', modulo: 'cadastros' },
        fora('segmentos', '/cadastros/cadastros/classificacao', 'cadastros.ver'),
        secao('motivos'),
        breve('infoPre'),
        secao('modelos-email', t('crm.ficha2.config.secoes.modelos-email')),
        breve('telefone'),
      ],
    },
    { rotulo: g('acompanhamento'), coluna: 2, itens: [fora('exportacoes', '/relatorios/relatorios/exportacoes', 'relatorios.exportar'), breve('acoesMassa'), fora('lixeira', '/conta/lixeira'), fora('logsErros', '/admin/admin/erros', 'nucleo.erros.ver')] },
  ];
  // Val1: o atalho de uma página negada (desmarcada no papel ou retirada da pessoa) também some. Review rval1-1 (SPEC-B2):
  // de qualquer módulo (Produtos do Estoque, Classificação, Conectores, Assinaturas, Contatos), não só os do Pipeline.
  const liberada = (i: ItemIndice) => {
    const pagina = i.para ? paginaDoAtalho(i.para) : null;
    return !pagina || (s.paginaLiberada?.(pagina.slice(0, pagina.indexOf('.')), pagina.slice(pagina.indexOf('.') + 1)) ?? true);
  };
  return grupos
    .map((x) => ({
      ...x,
      itens: x.itens
        .filter((i) => i.emBreve || !i.permissao || (typeof i.permissao === 'string' ? [i.permissao] : i.permissao).every((x) => s.pode(x)))
        .filter(liberada)
        .filter((i) => !i.modulo || s.instalado(i.modulo))
        .map((i) => ({ ...i, novo: ITENS_NOVOS.includes(i.chave) })),
    }))
    .filter((x) => x.itens.length);
}

/** Ajustes do CRM (27/10, K21): a página inicial das Configurações do CRM (`/crm/configuracoes`). */
export function IndiceConfiguracoes() {
  const { t } = useTranslation('pipeline');
  const id = useId();
  const [busca, setBusca] = useState('');
  const grupos = useGruposIndice();
  const norm = (x: string) => x.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  const filtrados = useMemo(() => grupos.map((g) => ({ ...g, itens: g.itens.filter((i) => !busca.trim() || norm(i.rotulo).includes(norm(busca.trim())) || norm(g.rotulo).includes(norm(busca.trim()))) })).filter((g) => g.itens.length), [grupos, busca]);
  const s = useSessao();
  // Lote crm7: as três sugestões da origem (campos, convidar a equipe e o playbook); sem acesso aos usuários, a do meio leva
  // aos modelos de e-mail, como antes.
  const sugestoes = [
    { texto: t('crm.ficha2.config.sugestoes.campos'), botao: t('crm.ficha2.config.sugestoes.camposBotao'), para: `${CAMINHO_CONFIGURACOES}/campos` },
    s.pode('admin.usuarios')
      ? { texto: t('crm.cfg.sugestoes.usuarios'), botao: t('crm.cfg.sugestoes.usuariosBotao'), para: '/pessoas/admin/usuarios' }
      : { texto: t('crm.ficha2.config.sugestoes.modelos'), botao: t('crm.ficha2.config.sugestoes.modelosBotao'), para: `${CAMINHO_CONFIGURACOES}/modelos-email` },
    { texto: t('crm.ficha2.config.sugestoes.playbook'), botao: t('crm.ficha2.config.sugestoes.playbookBotao'), para: `${CAMINHO_CONFIGURACOES}/funis` },
  ];
  return (
    <div className="flex min-w-0 flex-col gap-6" data-indice-configuracoes="">
      <section aria-labelledby={`${id}-sug`}>
        <h2 id={`${id}-sug`} className="mb-3 text-base font-semibold">
          {t('crm.ficha2.config.sugestoesTitulo')}
        </h2>
        <ul className="grid gap-3 md:grid-cols-3">
          {sugestoes.map((x) => (
            <li key={x.para} className="flex flex-col justify-between gap-3 rounded-xl border border-borda bg-acento-suave p-4">
              <p className="text-sm font-medium">{x.texto}</p>
              <Link to={x.para} className="w-fit rounded-lg bg-acento-forte px-3 py-1.5 text-sm font-semibold text-sup-1 hover:bg-acento-hover">
                {x.botao}
              </Link>
            </li>
          ))}
        </ul>
      </section>
      <div className="relative">
        <label htmlFor={`${id}-busca`} className="sr-only">
          {t('crm.ficha2.config.buscar')}
        </label>
        <input
          id={`${id}-busca`}
          type="search"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder={t('crm.ficha2.config.buscaPlaceholder')}
          className="h-11 w-full rounded-lg border border-borda-campo bg-campo pr-10 pl-3 text-sm placeholder:text-tinta-3 focus:border-foco focus:outline-2 focus:outline-offset-0 focus:outline-foco"
          data-busca-configuracoes=""
        />
        <Search className="pointer-events-none absolute top-3 right-3 size-5 text-tinta-3" aria-hidden />
      </div>
      {filtrados.length === 0 && <p className="text-sm text-tinta-3">{t('crm.ficha2.config.nada')}</p>}
      <div className="grid gap-6 md:grid-cols-3">
        {([0, 1, 2] as const).map((c) => (
          <div key={c} className="flex flex-col gap-6">
            {filtrados
              .filter((g) => g.coluna === c)
              .map((g) => (
                <section key={g.rotulo} aria-label={g.rotulo}>
                  <h3 className="kicker mb-2">{g.rotulo}</h3>
                  <ul className="flex flex-col gap-1">
                    {g.itens.map((i) => (
                      <li key={i.chave}>
                        {i.emBreve || !i.para ? (
                          <span className="flex flex-wrap items-center gap-2 text-sm font-medium text-tinta-3" aria-disabled="true" data-item-configuracao={i.chave} data-em-breve="" title={t('crm.ficha2.config.emBreveAjuda')}>
                            {i.rotulo}
                            <Selo>{t('crm.ficha2.config.emBreve')}</Selo>
                          </span>
                        ) : (
                          <span className="flex flex-wrap items-center gap-2">
                            <Link to={i.para} className="text-sm font-medium text-acento-forte hover:underline" data-item-configuracao={i.chave}>
                              {i.rotulo}
                            </Link>
                            {i.novo && <Selo cor="azul">{t('crm.cfg.novo')}</Selo>}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
          </div>
        ))}
      </div>
    </div>
  );
}

interface ModeloEmail {
  id: string;
  nome: string;
  assunto: string;
  corpo: string;
  corpo_html: string | null;
  compartilhado: boolean;
  meu: boolean;
  /** Lote crm6 (item 15): modelo pronto do CRM (não se edita; serve de base). */
  pronto?: boolean;
  created_at: string;
}

/**
 * Ajustes do CRM (27/10, F4 e K21): Modelos de e-mail (o "Modelo de Email" do RD), com as tags trocadas no envio.
 * Lote crm7: como na origem, "Adicionar modelo" abre o formulário na própria página, acima da tabela (vira "Fechar"), com as
 * tags ao lado; o lápis abre o mesmo formulário com o modelo; a tabela tem seleção e "Apagar os selecionados".
 */
export function ModelosEmailCrm() {
  const { t } = useTranslation('pipeline');
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['pipeline', 'email-modelos'], queryFn: () => get<{ itens: ModeloEmail[] }>('/pipeline/email-modelos') });
  const [editando, setEditando] = useState<ModeloEmail | 'novo' | { base: ModeloEmail } | null>(null);
  const [excluir, setExcluir] = useState<ModeloEmail[] | null>(null);
  const [sel, setSel] = useState<string[]>([]);
  // Rodada rlote-2 (VIS-M3): na casca nova só a área central rola (`window.scrollTo` não faz nada). Aberto pela linha da
  // tabela, o formulário (acima dela) entra na vista e o foco vai ao primeiro campo, também para o teclado e o leitor de tela.
  const [focarForm, setFocarForm] = useState(0);
  const abrirForm = (x: ModeloEmail | { base: ModeloEmail }) => {
    setEditando(x);
    setFocarForm((n) => n + 1);
  };
  useEffect(() => {
    if (!focarForm) return;
    const form = document.querySelector<HTMLElement>('[data-form-modelo-email]');
    form?.scrollIntoView?.({ block: 'start' });
    form?.querySelector<HTMLElement>('input, textarea, [contenteditable="true"]')?.focus();
  }, [focarForm]);
  const recarregar = () => qc.invalidateQueries({ queryKey: ['pipeline', 'email-modelos'] });
  const acao = useMutation({ mutationFn: (f: () => Promise<unknown>) => f(), onSuccess: recarregar, onError: (e) => toast.erro(mensagemDeErro(e)) });
  const itens = q.data?.itens ?? [];
  const selValida = sel.filter((id) => itens.some((m) => m.id === id));
  return (
    <section className="flex min-w-0 flex-col gap-5" data-config-modelos-email="">
      <CabecalhoConfig titulo={t('crm.ficha2.config.secoes.modelos-email')} intro={<p>{t('crm.cfg.modelosEmail.intro')}</p>} />
      <Botao
        variante={editando ? 'secundario' : 'primario'}
        className="self-start"
        icone={editando ? undefined : <Plus className="size-4" />}
        onClick={() => setEditando(editando ? null : 'novo')}
        data-criar-modelo-email=""
      >
        {editando ? t('crm.cfg.modelosEmail.fechar') : t('crm.cfg.modelosEmail.adicionar')}
      </Botao>
      {editando && (
        <FormModeloEmail
          key={editando === 'novo' ? 'novo' : 'base' in editando ? `base-${editando.base.id}` : editando.id}
          atual={editando === 'novo' || 'base' in editando ? null : editando}
          base={editando !== 'novo' && 'base' in editando ? editando.base : undefined}
          aoFechar={() => setEditando(null)}
          aoSalvar={recarregar}
        />
      )}
      <Estado consulta={q}>
        {(d) => (
          <div className="min-w-0 rounded-xl border border-borda bg-sup-1">
            {selValida.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 border-b border-borda bg-sup-2 px-3 py-2" data-lote-config="">
                <Botao tamanho="sm" variante="fantasma" icone={<Trash2 className="size-4" />} onClick={() => setExcluir(d.itens.filter((m) => selValida.includes(m.id)))} data-apagar-selecionados="">
                  {t('crm.cfg.modelosEmail.apagarSel', { count: selValida.length })}
                </Botao>
              </div>
            )}
            <div className={CLASSE_TABELA_CONFIG} role="region" tabIndex={0} aria-label={t('crm.ficha2.config.secoes.modelos-email')}>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-borda">
                    <th className={`${CLASSE_TH} w-10`}>
                      <CaixaConfig marcado={d.itens.some((m) => !m.pronto) && d.itens.every((m) => m.pronto || selValida.includes(m.id))} rotulo={t('crm.cfg.lista.selecionarTodos')} aoMudar={(v) => setSel(v ? d.itens.filter((m) => !m.pronto).map((m) => m.id) : [])} />
                    </th>
                    <th className={CLASSE_TH}>{t('crm.ficha2.config.modelosEmail.nome', { count: d.itens.length })}</th>
                    <th className={CLASSE_TH}>{t('crm.ficha2.config.modelosEmail.assunto')}</th>
                    <th className={CLASSE_TH}>{t('crm.ficha2.config.modelosEmail.compartilhado')}</th>
                    <th className={CLASSE_TH}>{t('crm.ficha2.config.modelosEmail.criadoEm')}</th>
                    <th className={`${CLASSE_TH} text-right`}>{t('crm.ficha2.config.modelosEmail.opcoes')}</th>
                  </tr>
                </thead>
                <tbody>
                  {d.itens.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-3 py-6 text-center text-tinta-3">
                        {t('crm.ficha2.config.modelosEmail.vazio')}
                      </td>
                    </tr>
                  )}
                  {d.itens.map((m) => (
                    <tr key={m.id} className="border-b border-borda last:border-b-0" data-modelo-email={m.nome} data-modelo-pronto={m.pronto ? '' : undefined}>
                      <td className={CLASSE_TD}>
                        {/* crm6 item 15: o modelo pronto do CRM não se altera nem se apaga; não entra na seleção. */}
                        {!m.pronto && <CaixaConfig marcado={selValida.includes(m.id)} rotulo={t('crm.cfg.lista.selecionar', { nome: m.nome })} aoMudar={(v) => setSel((s) => (v ? [...s, m.id] : s.filter((x) => x !== m.id)))} />}
                      </td>
                      <td className={`${CLASSE_TD} font-medium`}>
                        <span className="flex flex-wrap items-center gap-2">
                          {textoLivre(m.nome)}
                          {m.pronto && <Selo cor="azul">{t('crm.crm6.modelos.pronto')}</Selo>}
                        </span>
                      </td>
                      <td className={CLASSE_TD}>{textoLivre(m.assunto)}</td>
                      <td className={CLASSE_TD}>{m.compartilhado ? <Selo cor="azul">{t('crm.ficha2.config.modelosEmail.sim')}</Selo> : <Selo>{t('crm.ficha2.config.modelosEmail.nao')}</Selo>}</td>
                      <td className={`numero ${CLASSE_TD} text-tinta-3`}>{dataHora(m.created_at)}</td>
                      <td className={CLASSE_TD}>
                        <span className="flex justify-end gap-1">
                          {m.pronto ? (
                            <Botao tamanho="sm" variante="fantasma" aria-label={t('crm.crm6.modelos.usarComoBase', { nome: textoLivre(m.nome) })} title={t('crm.crm6.modelos.usarComoBase', { nome: textoLivre(m.nome) })} icone={<Copy className="size-4" />} onClick={() => abrirForm({ base: m })} data-usar-como-base={m.nome} />
                          ) : (
                            <>
                              <Botao tamanho="sm" variante="fantasma" aria-label={t('crm.ficha2.config.modelosEmail.editar', { nome: textoLivre(m.nome) })} title={t('crm.ficha2.config.modelosEmail.editar', { nome: textoLivre(m.nome) })} icone={<Pencil className="size-4" />} onClick={() => abrirForm(m)} />
                              <Botao tamanho="sm" variante="fantasma" aria-label={t('crm.ficha2.config.modelosEmail.excluir', { nome: textoLivre(m.nome) })} title={t('crm.ficha2.config.modelosEmail.excluir', { nome: textoLivre(m.nome) })} icone={<Trash2 className="size-4" />} onClick={() => setExcluir([m])} />
                            </>
                          )}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Estado>
      <Confirmar
        aberto={!!excluir}
        aoFechar={() => setExcluir(null)}
        perigoso
        titulo={excluir?.length === 1 ? t('crm.ficha2.config.modelosEmail.confirmarExcluir', { nome: textoLivre(excluir[0]!.nome) }) : t('crm.cfg.modelosEmail.confirmarApagar', { count: excluir?.length ?? 0 })}
        texto={t('crm.ficha2.config.modelosEmail.confirmarExcluirTexto')}
        aoConfirmar={() => {
          const l = excluir ?? [];
          setExcluir(null);
          if (editando && editando !== 'novo' && !('base' in editando) && l.some((m) => m.id === editando.id)) setEditando(null);
          acao.mutate(async () => {
            for (const m of l) await del(`/pipeline/email-modelos/${m.id}`);
            setSel((s) => s.filter((x) => !l.some((m) => m.id === x)));
          });
        }}
      />
    </section>
  );
}

/** O formulário do modelo (criar, editar ou, com `base`, criar a partir de um pronto). Também abre do "Criar e-mail" (crm6 item 16). */
export function FormModeloEmail({ atual, base, aoFechar, aoSalvar }: { atual: ModeloEmail | null; base?: ModeloEmail; aoFechar: () => void; aoSalvar: (id: string) => Promise<unknown> | void }) {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const id = useId();
  const origem = atual ?? base ?? null;
  const [nome, setNome] = useState(atual?.nome ?? (base ? t('crm.crm6.modelos.copiaNome', { nome: base.nome }) : ''));
  const [assunto, setAssunto] = useState(origem?.assunto ?? '');
  // Lote crm4 (L8): o corpo pelo editor com formatação (o modelo de antes do editor entra em parágrafos).
  const [corpo, setCorpo] = useState(() => htmlDoCampo(origem?.corpo_html, origem?.corpo ?? ''));
  const [compartilhado, setCompartilhado] = useState(atual?.compartilhado ?? true);
  const [erros, setErros] = useState<{ nome?: string; assunto?: string }>({});
  const salvar = useMutation({
    mutationFn: () => {
      const corpoReq = { nome: nome.trim(), assunto: assunto.trim(), corpo_html: corpo, compartilhado };
      return atual ? patch(`/pipeline/email-modelos/${atual.id}`, corpoReq).then(() => ({ id: atual.id })) : post<{ id: string }>('/pipeline/email-modelos', corpoReq);
    },
    onSuccess: async (r) => {
      toast.sucesso(t('crm.ficha2.config.modelosEmail.salvo'));
      await aoSalvar(r?.id ?? '');
      aoFechar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  // A tag entra no fim do último parágrafo (ou num parágrafo novo, com o corpo vazio).
  const inserirTag = (tag: string) => setCorpo((c) => (/<\/p>$/.test(c) ? c.replace(/<\/p>$/, `{{${tag}}}</p>`) : `${c}<p>{{${tag}}}</p>`));
  return (
    <div className="flex min-w-0 flex-col gap-4 lg:flex-row lg:items-start" data-form-modelo-email="">
      <form
        noValidate
        className="flex min-w-0 flex-1 flex-col gap-4 rounded-xl border border-borda bg-sup-1 p-4"
        aria-label={atual ? t('crm.ficha2.config.modelosEmail.editarTitulo') : t('crm.ficha2.config.modelosEmail.criar')}
        onSubmit={(e) => {
          e.preventDefault();
          const er = { ...(nome.trim() ? {} : { nome: tn('erros.obrigatorio') }), ...(assunto.trim() ? {} : { assunto: tn('erros.obrigatorio') }) };
          setErros(er);
          if (!Object.keys(er).length) salvar.mutate();
        }}
      >
        <Rotulo id={`${id}-nome`} rotulo={t('crm.ficha2.config.modelosEmail.nomeCampo')} obrigatorio erro={erros.nome}>
          <EntradaSensivel id={`${id}-nome`} mascara={null} livre maxLength={120} value={nome} onChange={(e) => setNome(e.target.value)} aria-invalid={!!erros.nome} />
        </Rotulo>
        <Rotulo id={`${id}-assunto`} rotulo={t('crm.ficha2.config.modelosEmail.assunto')} obrigatorio erro={erros.assunto}>
          <EntradaSensivel id={`${id}-assunto`} mascara={null} livre maxLength={200} value={assunto} onChange={(e) => setAssunto(e.target.value)} aria-invalid={!!erros.assunto} />
        </Rotulo>
        <Rotulo id={`${id}-corpo`} rotulo={t('crm.ficha2.config.modelosEmail.corpo')}>
          <EditorRico id={`${id}-corpo`} rotulo={t('crm.ficha2.config.modelosEmail.corpo')} linhas={10} valor={corpo} aoMudar={setCorpo} dado="modelo-email" />
        </Rotulo>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-3 text-sm font-medium">
            {t('crm.ficha2.config.modelosEmail.compartilhado')}
            <Alternador marcado={compartilhado} aoMudar={setCompartilhado} rotulo={t('crm.ficha2.config.modelosEmail.compartilhado')} />
          </label>
          <span className="min-w-0 flex-1 basis-48 text-xs text-tinta-3">{t('crm.ficha2.config.modelosEmail.compartilhadoAjuda')}</span>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao type="submit" variante="primario" carregando={salvar.isPending} data-salvar-modelo-email="">
            {tn('comum.salvar')}
          </Botao>
        </div>
      </form>
      <div className="flex flex-col gap-1 rounded-xl border border-borda bg-sup-1 p-4 text-sm lg:w-64 lg:shrink-0" data-tags-email="">
        <p className="font-semibold">{t('crm.ficha2.config.modelosEmail.tags')}</p>
        <p className="text-xs text-tinta-3">{t('crm.ficha2.config.modelosEmail.tagsAjuda')}</p>
        <ul className="mt-1 flex flex-col gap-1">
          {TAGS_EMAIL.map((tag) => (
            <li key={tag} className="flex flex-wrap items-center gap-2">
              <span>{t(`crm.ficha2.config.modelosEmail.tag.${tag}`)}:</span>
              <button type="button" className="rounded bg-sup-3 px-1.5 py-0.5 font-mono text-xs hover:bg-sup-2" onClick={() => inserirTag(tag)} title={t('crm.ficha2.config.modelosEmail.inserirTag')}>
                {`{{${tag}}}`}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/**
 * Ajustes do CRM (27/10, F12): Preferências do CRM; por ora, o parabéns depois de marcar a venda (ligado por padrão).
 * Lote crm7: as cinco abas da origem; só "Negociação" existe no MalgoOS e abre direto, as outras aparecem "Em breve".
 */
export function PreferenciasCrm() {
  const { t } = useTranslation('pipeline');
  const qc = useQueryClient();
  const toast = useToast();
  const s = useSessao();
  const listas = useListas();
  const salvar = useMutation({
    mutationFn: (v: boolean) => api('PUT', '/pipeline/crm/listas', { celebrar_venda: v }),
    onSuccess: async () => {
      toast.sucesso(t('crm.configuracoes.salvo'));
      await qc.invalidateQueries({ queryKey: ['pipeline', 'crm', 'listas'] });
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  return (
    <section className="flex min-w-0 flex-col gap-5" data-config-preferencias="">
      <CabecalhoConfig
        titulo={t('crm.ficha2.config.secoes.preferencias')}
        abas={abasPreferencias(t)}
        ativa="negociacao"
      />
      <LinhaAlternador
        texto={t('crm.cfg.preferencias.celebrar')}
        ajuda={t('crm.cfg.preferencias.celebrarAjuda')}
        marcado={listas.data?.celebrar_venda !== false}
        desabilitado={!listas.data || salvar.isPending || !s.pode('pipeline.configurar')}
        aoMudar={(v) => salvar.mutate(v)}
        dado="celebrar"
      />
    </section>
  );
}
